import moment from 'moment';

export const DATE_FORMAT = 'YYYY-MM-DD';

export const PARAM_ACCOUNT_ID = 'account_id';
export const PARAM_WIDGET_ID = 'widget_id';
export const PARAM_DATE_FROM = 'date_from';
export const PARAM_DATE_TO = 'date_to';
export const PARAM_PAGE = 'page';

export const DATE_RANGES = {
    TODAY: 1,
    WEEK: 7,
    FOUR_WEEKS: 28,
    TWELVE_WEEKS: 84,
};

export const DATE_RANGE_OPTIONS = [
    { label: 'Today', value: DATE_RANGES.TODAY },
    { label: 'Last 7 days', value: DATE_RANGES.WEEK },
    { label: 'Last 28 days', value: DATE_RANGES.FOUR_WEEKS },
    { label: 'Last 12 weeks', value: DATE_RANGES.TWELVE_WEEKS },
];

export const DEFAULT_DATE_RANGE = DATE_RANGES.WEEK;

export const GROUPING_HOURLY = 'hourly';
export const GROUPING_DAILY = 'daily';
export const GROUPING_WEEKLY = 'weekly';
export const GROUPING_MONTHLY = 'monthly';

const isValidDate = (value) => moment(value, DATE_FORMAT, true).isValid();

export const today = () => moment().format(DATE_FORMAT);

export const presetRange = (days) => ({
    from: moment()
        .subtract(days - 1, 'days')
        .format(DATE_FORMAT),
    to: today(),
});

export const dayCount = (from, to) =>
    moment(to, DATE_FORMAT).diff(moment(from, DATE_FORMAT), 'days') + 1;

export const detectPreset = (from, to) => {
    if (to !== today()) {
        return null;
    }

    const days = dayCount(from, to);

    return Object.values(DATE_RANGES).includes(days) ? days : null;
};

export const dateGrouping = (from, to) => {
    const days = dayCount(from, to);

    if (days <= DATE_RANGES.TODAY) {
        return GROUPING_HOURLY;
    }

    if (days <= DATE_RANGES.WEEK) {
        return GROUPING_DAILY;
    }

    if (days <= DATE_RANGES.TWELVE_WEEKS) {
        return GROUPING_WEEKLY;
    }

    return GROUPING_MONTHLY;
};

export const intervalForGrouping = (grouping) =>
    grouping === GROUPING_HOURLY ? GROUPING_HOURLY : GROUPING_DAILY;

export const parseFiltersFromParams = (params = {}) => {
    const fallback = presetRange(DEFAULT_DATE_RANGE);
    const from = isValidDate(params[PARAM_DATE_FROM])
        ? params[PARAM_DATE_FROM]
        : fallback.from;
    const to = isValidDate(params[PARAM_DATE_TO])
        ? params[PARAM_DATE_TO]
        : fallback.to;
    const page = Number(params[PARAM_PAGE]);

    return {
        accountId: params[PARAM_ACCOUNT_ID] || null,
        widgetId: params[PARAM_WIDGET_ID] || null,
        from: moment(to, DATE_FORMAT).isBefore(moment(from, DATE_FORMAT))
            ? to
            : from,
        to,
        page: Number.isInteger(page) && page > 0 ? page : 1,
    };
};

export const buildUrlParams = (filters = {}) => ({
    [PARAM_ACCOUNT_ID]: filters.accountId || undefined,
    [PARAM_WIDGET_ID]: filters.widgetId || undefined,
    [PARAM_DATE_FROM]: filters.from,
    [PARAM_DATE_TO]: filters.to,
    [PARAM_PAGE]: filters.page && filters.page > 1 ? filters.page : undefined,
});

export const toAccountId = (accountId) => {
    const id = Number(accountId);
    return Number.isInteger(id) && id > 0 ? id : null;
};

export const toApiTimestamp = (date, endOfDay = false) => {
    if (!date) {
        return date;
    }

    return endOfDay ? `${date}T23:59:59Z` : `${date}T00:00:00Z`;
};

export const buildApiParams = (filters = {}) => {
    const params = {
        from: toApiTimestamp(filters.from),
        to: toApiTimestamp(filters.to, true),
        group: intervalForGrouping(dateGrouping(filters.from, filters.to)),
    };

    const accountId = toAccountId(filters.accountId);
    if (accountId) {
        params.account_id = accountId;
    }

    if (filters.widgetId) {
        params.widget_id = filters.widgetId;
    }

    return params;
};

export const hasActiveFilters = (filters = {}) =>
    Boolean(filters.accountId || filters.widgetId);

export const filterSummary = (filters = {}, options = {}) => {
    const parts = [];

    if (filters.accountId) {
        const account = (options.accounts || []).find(
            (item) => item.id === filters.accountId,
        );
        parts.push(account ? account.name : `Account ${filters.accountId}`);
    }

    if (filters.widgetId) {
        const widget = (options.widgets || []).find(
            (item) => item.id === filters.widgetId,
        );
        parts.push(widget ? widget.name : `Widget ${filters.widgetId}`);
    }

    return parts.length ? parts.join(' · ') : null;
};

export const widgetOptionsForAccount = (widgets = [], accountId) =>
    accountId
        ? widgets.filter((widget) => widget.accountId === accountId)
        : widgets;
